"use client";

import { cn } from "@/lib/utils";
import { Link } from "@/i18n/navigation";
import { ArrowRight } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import type { ReactNode } from "react";
import { SCROLL_TILE_WIDTH } from "@/lib/constants";

export interface TileSectionProps {
  title: string;
  children: ReactNode;
  seeAllHref?: string;
  seeAllLabel?: string;
  isLoading?: boolean;
  isEmpty?: boolean;
  className?: string;
}

export function TileSection({
  title,
  children,
  seeAllHref,
  seeAllLabel,
  isLoading,
  isEmpty,
  className,
}: TileSectionProps) {
  if (isEmpty && !isLoading) return null;

  return (
    <section className={cn("py-8 sm:py-10", className)}>
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl sm:text-2xl font-bold tracking-tight">{title}</h2>
          {seeAllHref && seeAllLabel && (
            <Link
              href={seeAllHref}
              className="text-sm font-medium text-primary hover:text-primary/80 transition-colors flex items-center gap-1"
            >
              {seeAllLabel}
              <ArrowRight className="h-4 w-4" />
            </Link>
          )}
        </div>
        {isLoading ? (
          <div className="-mr-4 md:mr-0 flex gap-3 overflow-hidden pr-4 md:pr-0">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className={`shrink-0 ${SCROLL_TILE_WIDTH} h-36 rounded-xl`} />
            ))}
          </div>
        ) : (
          children
        )}
      </div>
    </section>
  );
}
